import { useEffect, useMemo, useRef } from "react";
import { getRouteApi } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";

export const paginationSearchSchema = {
  page: fallback(z.number().int().min(1), 1).default(1),
  pageSize: fallback(z.union([z.literal(50), z.literal(100), z.literal(200)]), 50).default(50),
};

export const paginationValidator = zodValidator(z.object(paginationSearchSchema));

/**
 * Client-side pagination bound to URL search params (?page, ?pageSize).
 * Pass the pre-filtered list (or the full list) and a stable key that
 * changes when filters change — the hook resets to page 1.
 */
export function useTablePagination<T>(
  routeId: Parameters<typeof getRouteApi>[0],
  items: T[],
  filtersKey: string = "",
) {
  const routeApi = getRouteApi(routeId as any);
  const search = (routeApi as any).useSearch() as { page: number; pageSize: number };
  const navigate = (routeApi as any).useNavigate();
  const { page, pageSize } = search;

  const lastKey = useRef(filtersKey);
  useEffect(() => {
    if (lastKey.current !== filtersKey) {
      lastKey.current = filtersKey;
      if (page !== 1) navigate({ search: (prev: any) => ({ ...prev, page: 1 }) });
    }
  }, [filtersKey, page, navigate]);

  const total = items.length;
  const paged = useMemo(
    () => items.slice((page - 1) * pageSize, page * pageSize),
    [items, page, pageSize],
  );

  const onPageChange = (p: number) =>
    navigate({ search: (prev: any) => ({ ...prev, page: p }) });
  const onPageSizeChange = (s: number) =>
    navigate({ search: (prev: any) => ({ ...prev, pageSize: s, page: 1 }) });

  return { page, pageSize, total, paged, onPageChange, onPageSizeChange };
}
